import React, { useEffect, useState } from "react";
import { Checkbox, Col, Form, InputNumber, Modal, Row, Select, Space, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { decimalInputProps, euroToCents } from "../workReports/format";
import { estimate } from "./api";
import EstimateView from "./EstimateView";

/** Quick subsidy estimate without a case (e.g. on the phone at the customer's kitchen table). */
const CalculatorModal = ({ open, meta, onClose }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const values = Form.useWatch([], form);
  const program = meta?.programs.find(p => p.key === values?.program);

  useEffect(() => {
    if (open && !form.getFieldValue("program")) {
      form.setFieldsValue({
        program: "kfw_458",
        costs: 30000,
        units: 1,
        ownerOccupier: true,
        bonuses: [],
        measures: []
      });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !values?.program) return undefined;
    const timer = setTimeout(() => {
      estimate({
        program: values.program,
        eligibleCents: euroToCents(values.costs),
        units: values.units || 1,
        bonuses: values.bonuses || [],
        flags: { isfp: Boolean(values.isfp) },
        ownerOccupier: values.ownerOccupier !== false,
        measures: values.measures || [],
        advisorCents: euroToCents(values.advisor),
        completionYear: values.year || null
      })
        .then(res => setResult(res.data))
        .catch(generalErrorAlert);
    }, 300);
    return () => clearTimeout(timer);
  }, [open, JSON.stringify(values)]);

  const choosable = (program?.bonuses || []).filter(b => !b.requiresFlag);
  const hasIsfp = (program?.bonuses || []).some(b => b.requiresFlag === "isfp");
  const tax = program?.kind === "tax";

  return (
    <Modal
      rootClassName="funding-page"
      open={open}
      width={720}
      title={t("Funding calculator")}
      footer={null}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="program" label={t("Programme")}>
          <Select
            options={(meta?.programs || []).map(p => ({ value: p.key, label: p.name }))}
            onChange={() => form.setFieldsValue({ bonuses: [], measures: [] })}
          />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="costs" label={tax ? t("Costs incl. VAT") : t("Eligible costs")}>
              <InputNumber
                className="fu-full"
                min={0}
                precision={2}
                addonAfter="€"
                {...decimalInputProps(i18n.language)}
              />
            </Form.Item>
          </Col>
          {tax ? (
            <>
              <Col xs={12} md={6}>
                <Form.Item name="advisor" label={t("Energy advisor costs")}>
                  <InputNumber
                    className="fu-full"
                    min={0}
                    precision={2}
                    addonAfter="€"
                    {...decimalInputProps(i18n.language)}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="year" label={t("Year of completion")}>
                  <InputNumber className="fu-full" min={2020} max={2100} precision={0} />
                </Form.Item>
              </Col>
            </>
          ) : (
            <>
              <Col xs={12} md={6}>
                <Form.Item name="units" label={t("Dwelling units")}>
                  <InputNumber className="fu-full" min={1} max={999} precision={0} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="ownerOccupier" label={t("Owner-occupied")} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        {!tax && (program?.measures || []).length > 0 && (
          <Form.Item name="measures" label={t("Measures")}>
            <Select
              mode="multiple"
              options={program.measures.map(m => ({ value: m.key, label: m.label }))}
            />
          </Form.Item>
        )}
        {!tax && (choosable.length > 0 || hasIsfp) && (
          <Space direction="vertical" className="fu-full">
            {choosable.length > 0 && (
              <Form.Item name="bonuses" label={t("Confirmed bonus conditions")}>
                <Checkbox.Group className="fu-checks">
                  {choosable.map(b => (
                    <Checkbox key={b.key} value={b.key}>
                      {b.label} ({b.flatCents ? `${b.flatCents / 100} €` : `${b.percent} %`})
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            )}
            {hasIsfp && (
              <Form.Item name="isfp" valuePropName="checked">
                <Checkbox>
                  {t("Measure is part of an individual renovation roadmap (iSFP)")}
                </Checkbox>
              </Form.Item>
            )}
          </Space>
        )}
      </Form>
      {result && <EstimateView estimate={result.estimate} disclaimer={result.disclaimer} />}
    </Modal>
  );
};

export default CalculatorModal;
